import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import type React from "react";
import { useState } from "react";

interface GenerateCSVAndEmailWithRangeProps {
  generateCSV: (from: Date, to: Date) => Promise<void>;
}

export const GenerateCSVAndEmailWithRange: React.FC<
  GenerateCSVAndEmailWithRangeProps
> = ({ generateCSV }) => {
  const [open, setOpen] = useState(false);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [error, setError] = useState("");
  const [showEmailNote, setShowEmailNote] = useState(false);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setError("");
    setShowEmailNote(false);
  };

  const handleDownload = async () => {
    if (!fromDate || !toDate) {
      setError("Both date fields must be filled.");
      return;
    }
    if (toDate < fromDate) {
      setError('The "To" date cannot be earlier than the "From" date.');
      return;
    }
    await generateCSV(fromDate, toDate);
    setShowEmailNote(true);
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<FileDownloadIcon />}
        onClick={handleClickOpen}
        sx={{
          width: 55,
          height: 55,
          minHeight: 55,
          minWidth: 55,
          borderRadius: "50%",
          padding: 0,
          "& .MuiButton-startIcon": { margin: 0 },
        }}
      />

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <p>
            Select a date range to filter the data included in this CSV
            download.
          </p>
          <Stack direction="row" spacing={2} mt={5} mb={2}>
            <DatePicker
              label="From"
              value={fromDate}
              maxDate={new Date()}
              onChange={(newValue) => setFromDate(newValue)}
            />
            <DatePicker
              label="To"
              value={toDate}
              maxDate={new Date()}
              onChange={(newValue) => setToDate(newValue)}
            />
          </Stack>
          {error && <Typography style={{ color: "red" }}>{error}</Typography>}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          {showEmailNote ? (
            <Typography
              sx={{
                backgroundColor: "#E8F5E9",
                color: "#4CAF50",
                padding: 2,
                textAlign: "center",
                borderRadius: 1,
              }}
            >
              Your report is on its way to your email inbox.
            </Typography>
          ) : (
            <Button variant="contained" onClick={handleDownload}>
              Generate CSV
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
